import React, { useRef, useEffect, useState } from 'react';
import { components } from 'react-select';
import PropTypes from 'prop-types';
import { colors } from './Styles';

/**
 * Custom group component.
 *
 * @param {object} props - Props for the Group component.
 * @returns {JSX.Element} Rendered Group component.
 */
export const Group = (props) => {
  return (
    <components.Group
      {...props}
      headingProps={{
        ...props.headingProps,
      }}
    />
  );
};

/**
 * Custom group heading component.
 *
 * @param {object} props - Props for the GroupHeading component.
 * @returns {JSX.Element} Rendered GroupHeading component.
 */
// Define the GroupHeading component using ES6 destructuring for props
export const GroupHeading = ({ ...props }) => {
  // Define a style for the selected group
  const defaultSelectedStyle = {
    backgroundColor: colors.primary50,
    color: colors.neutral80,
  };

  // Find matching selected options within the group
  const matches = props.selectProps.value.filter((selectedOptions) =>
    props.data.options.some((dataOptions) => dataOptions.value === selectedOptions.value),
  );

  // Determine if the group is in an indeterminate state
  const indeterminate = matches.length > 0 && matches.length !== props.data.options.length;

  // Determine if the group is fully checked
  const checked = matches.length === props.data.options.length;

  // Apply the selected style if the group is either indeterminate or fully checked
  const style = {
    ...((indeterminate || checked) &&
      (props.selectProps.styles['groupHeading']().selectedStyle || defaultSelectedStyle)),
  };

  /**
   * Handle group selection.
   *
   * @param {boolean} checked - Indicates if the group is checked.
   */
  const handleGroupSelect = (checked) => {
    // Call the provided handleGroupSelect function with appropriate parameters
    props.selectProps.handleGroupSelect(props.data, props.data.label, checked, props.selectProps.onChange); // Use the prop here
  };

  // Render the group heading along with the checkbox
  return (
    <components.GroupHeading {...props} style={style}>
      <IndeterminateCheckbox readOnly checked={checked} indeterminate={indeterminate} onChange={handleGroupSelect} />
      {props.children}
    </components.GroupHeading>
  );
};

/**
 * Custom option component.
 *
 * @param {object} props - Props for the Option component.
 * @returns {JSX.Element} Rendered Option component.
 */
// Define the Option component using ES6 destructuring for props
export const Option = ({ ...props }) => {
  return (
    // Render the option using the components.Option from the imported library
    <components.Option {...props}>
      {/* Render an input checkbox */}
      <input
        readOnly
        type="checkbox"
        checked={props.isSelected}
        onClick={() => props.selectOption(props.data)}
        style={{ marginRight: '8px' }}
      />
      {/* Render the label of the option */}
      <label style={{ margin: '0px' }}>{props.data.label}</label>
    </components.Option>
  );
};

/**
 * A custom checkbox component that supports indeterminate state.
 *
 * @param {object} props - Props for the IndeterminateCheckbox component.
 * @param {boolean} props.indeterminate - Indicates if the checkbox is in an indeterminate state.
 * @param {boolean} props.checked - Indicates if the checkbox is checked.
 * @param {function} props.onChange - Callback function when the checkbox value changes.
 * @returns {JSX.Element} Rendered IndeterminateCheckbox component.
 */
// Define the IndeterminateCheckbox component using ES6 destructuring for props
export const IndeterminateCheckbox = ({ indeterminate, checked, onChange, ...rest }) => {
  // Create a ref to the checkbox element
  const checkboxRef = useRef(null);

  // Create state for internal checked state
  const [internalChecked, setInternalChecked] = useState(checked);

  // Effect to set the "indeterminate" property of the checkbox
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  // Effect to synchronize the internal checked state with the "checked" prop
  useEffect(() => {
    setInternalChecked(checked);
  }, [checked]);

  // Click handler for the checkbox
  const handleClick = () => {
    if (indeterminate) {
      setInternalChecked(true);
      onChange(true);
    } else {
      setInternalChecked(!internalChecked);
      onChange(!internalChecked);
    }
  };

  // Render the input checkbox
  return (
    <input
      {...rest}
      type="checkbox"
      style={{ marginRight: 8 }}
      ref={checkboxRef}
      checked={internalChecked}
      onChange={handleClick}
    />
  );
};

// PropTypes definitions below...
Group.propTypes = {
  components: PropTypes.shape({
    Group: PropTypes.elementType.isRequired,
  }),
};

GroupHeading.propTypes = {
  children: PropTypes.node.isRequired,
  selectProps: PropTypes.shape({
    value: PropTypes.array.isRequired,
    handleGroupSelect: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.any.isRequired,
      }),
    ).isRequired,
  }).isRequired,
};

Option.propTypes = {
  isSelected: PropTypes.bool.isRequired,
  selectOption: PropTypes.func.isRequired,
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
  }).isRequired,
};

IndeterminateCheckbox.propTypes = {
  indeterminate: PropTypes.bool,
};
